import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { useLocaleState, useRedirect } from 'react-admin'
import { useDispatch, useSelector } from 'react-redux'
import classnames from 'classnames'
import { usePermissions } from '@hooks/useHasPermission'
import { ACTIVATE_COMPANY_LOGO_WHITE } from '@/global'
import { getPropertyDetail } from '@api'
import { AccountSearch } from '@/dashboard/AdminSearch'
import { PropertySearch } from '@/dashboard/PropertySearchBar'
import { AppContext } from '@/context/AppContext'
import { selectedGroup, selectedProperty, setManagementTag } from '@reducer/profile'
import { IEntity } from '../types'
import HelpCircleIcon from '@mui/icons-material/HelpOutline'
import ExpandMore from '@mui/icons-material/ExpandMore'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Close'
import WarningIcon from '@mui/icons-material/WarningAmber'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import { matchesTokens, searchTokens } from '@utils/tokenSearch'
import DashboardThemeToggle from './DashboardThemeToggle'
import CustomUserMenu from '@/layout/CustomUserMenu'
import LogoutButton from '@component/Auth/LogoutButton'
import {
  CATEGORY_CONFIG,
  PORTFOLIO_CARD,
  PORTFOLIO_ANALYTICS_CARD,
  ACTIVATE_CARD,
  ACTIVATE_ADMIN_CARD,
  // BUILDING_CARD,
  ADD_ICON as AddIcon,
  AddButton,
  Category,
  NavItem,
} from './dashboardConfig'
import ApiManagerDialog from './ApiManagerDialog'
import NewEquipmentDialog from './NewEquipmentDialog'
import WelcomeNoPortfolio from './WelcomeNoPortfolio'

interface DashboardViewProps {
  onToggleTheme?: () => void
  isNewTheme?: boolean
}

/** Category title sets used for conditional grid/card styling */
const FOUR_COL_CATEGORIES = new Set(['BUILDING INFO', 'WORK ORDERS', 'PERFORMANCE', 'ANALYTICS'])
const THREE_COL_CATEGORIES = new Set(['BUILDING ACCESS', 'EQUIPMENT'])
const FIXED_HEIGHT_CATEGORIES = new Set([
  'BUILDING INFO',
  'ANALYTICS',
  'EQUIPMENT',
  'PERFORMANCE',
  'WORK ORDERS',
  'BUILDING ACCESS',
])

/** Reusable inline style objects hoisted to module scope for referential stability */
const GRID_STYLE_ACTIVATE: React.CSSProperties = { gridTemplateColumns: 'minmax(0, 1100px)', justifyContent: 'center' }
const GRID_STYLE_PORTFOLIO: React.CSSProperties = { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }

const CARD_STYLE_ACTIVATE: React.CSSProperties = { height: 'auto', minHeight: 700, maxHeight: 'none' }
// Short Activate-view cards (Activate Admin) hug their tiles instead.
const CARD_STYLE_ACTIVATE_COMPACT: React.CSSProperties = { height: 'auto', minHeight: 0, maxHeight: 'none' }
const CARD_STYLE_PORTFOLIO: React.CSSProperties = { height: 'auto', minHeight: 500, maxHeight: 'none' }
const CARD_STYLE_FIXED: React.CSSProperties = { height: 420, minHeight: 420, maxHeight: 420 }
// While a search is active every card hugs its filtered tiles — a fixed-height
// card with two hits reads as mostly empty space.
const CARD_STYLE_SEARCH: React.CSSProperties = { height: 'auto', minHeight: 0, maxHeight: 'none' }

const SEARCH_ADORNMENT = (
  <InputAdornment position="start">
    <SearchIcon fontSize="small" style={{ color: '#7C3AED' }} />
  </InputAdornment>
)

// minmax(0, 120px) tracks: 120px when the card has room, shrinking instead of
// overflowing when it doesn't — fixed-width tracks overflowed the card's
// overflow:hidden and clipped the outer tile columns on both edges.
const ITEM_GRID_ACTIVATE: React.CSSProperties = {
  gridTemplateColumns: 'repeat(7, minmax(0, 120px))',
  overflowY: 'visible',
  justifyContent: 'center',
  alignContent: 'center',
}
const ITEM_GRID_PORTFOLIO_5COL: React.CSSProperties = {
  gridTemplateColumns: 'repeat(5, minmax(0, 120px))',
  overflowY: 'visible',
  justifyContent: 'center',
  alignContent: 'center',
}
const ITEM_GRID_PORTFOLIO_4COL: React.CSSProperties = {
  gridTemplateColumns: 'repeat(4, minmax(0, 120px))',
  overflowY: 'visible',
  justifyContent: 'center',
  alignContent: 'center',
}

const STANDARD_ITEM_SIZE: React.CSSProperties = { width: '100%', height: 120 }
const STANDARD_ICON_SIZE: React.CSSProperties = { width: 40, height: 40, borderRadius: 10 }
const FLEX_GAP_8: React.CSSProperties = { display: 'flex', gap: 8 }
// Scope pickers share the header row with the logo, view toggle, theme toggle and
// user menu, so they give way first: each shrinks from 260px down to 150px before
// the row is allowed to wrap (see headerInner), instead of a fixed 260px that
// pushed the theme toggle and user menu onto a second row at 100% zoom.
const SELECTOR_STYLE: React.CSSProperties = { flex: '1 1 160px', minWidth: 150, maxWidth: 260 }

const ADD_ICON_SMALL = <AddIcon fontSize="small" />

// Launcher tooltips (category descriptions, why a tile is disabled): MUI's default
// is 10px grey-on-grey, unreadable at a glance, so they get body-size text on
// near-black with an arrow pointing at what they describe.
const TOOLTIP_PROPS = {
  arrow: true,
  componentsProps: {
    tooltip: {
      sx: {
        fontFamily: 'Roboto',
        fontSize: 16,
        fontWeight: 500,
        lineHeight: 1.45,
        color: '#FFFFFF',
        backgroundColor: '#1F1A14',
        padding: '10px 14px',
        borderRadius: '8px',
        maxWidth: 360,
        boxShadow: '0 6px 18px rgba(31,26,20,0.25)',
      },
    },
    arrow: { sx: { color: '#1F1A14' } },
  },
} as const

/** Wraps children in a disabled Tooltip + span when a reason string is provided */
const MaybeTooltip: React.FC<{
  reason: string
  className?: string
  children: React.ReactElement
}> = React.memo(({ reason, className, children }) =>
  reason ? (
    <Tooltip title={reason} placement="top" {...TOOLTIP_PROPS}>
      <span className={className}>{children}</span>
    </Tooltip>
  ) : (
    children
  )
)

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #FFF9EC 0%, #FFFDF7 100%)',
    padding: '0 24px 40px',
  },
  header: {
    margin: '0 -24px 28px',
    padding: '20px 24px 24px',
    // Same indigo-to-lavender as the admin app bar (layout/AppBarExtend.tsx), so the
    // One Click and Legacy views share one banner colour.
    background: 'linear-gradient(90deg, #4A3D9E 0%, #8B7FE0 100%)',
    color: '#fff',
    position: 'relative',
    boxShadow: '0 16px 30px rgba(74, 61, 158, 0.25)',
  },
  // One row: the pickers shrink (SELECTOR_STYLE) rather than the row wrapping. Only a
  // viewport too narrow for even the shrunken pickers wraps the meta cluster under.
  headerInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    gap: 16,
    '@media (max-width: 1100px)': {
      flexWrap: 'wrap',
    },
  },
  selectors: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flex: '1 1 auto',
    minWidth: 0,
  },
  selectorInput: {
    borderRadius: 10,
    '&[class*=MuiOutlinedInput-root]': {
      borderRadius: 10,
      backgroundColor: '#fff',
    },
  },
  // The Property Management tag filter is a bare Autocomplete (not
  // AccountSearch/PropertySearch), so it needs their base input look (white,
  // 48px) on top of the shared selectorInput radius.
  managementTagInput: {
    height: 48,
    backgroundColor: '#fff',
    '&[class*=MuiOutlinedInput-root]': {
      padding: '0 9px',
    },
  },
  logoCluster: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    flex: '1 1 auto',
    minWidth: 0,
  },
  headerMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    flexShrink: 0,
  },
  apiButton: {
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 700,
    padding: '6px 16px',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.65)',
    backgroundColor: 'rgba(255,255,255,0.12)',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.22)',
    },
  },
  headerLogo: {
    height: 60,
    width: 'auto',
  },
  logoButton: {
    borderRadius: 12,
    padding: 4,
    flexShrink: 0,
  },
  searchBar: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 24,
  },
  searchInput: {
    // Fills most of the row rather than a fixed 420px: this is the page's primary
    // control, and the tile grid below it is full-width.
    width: '75%',
    maxWidth: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 999,
      backgroundColor: '#fff',
      boxShadow: '0 8px 18px rgba(124, 58, 237, 0.12)',
      // Half again the 40px a size="small" outlined input would be. The root is a
      // flex row with centred items, so the input and both adornments recentre
      // themselves against the taller box without touching their own padding.
      height: 60,
    },
  },
  notice: {
    background: 'rgba(124, 58, 237, 0.08)',
    border: '1px dashed rgba(124,58,237,0.4)',
    borderRadius: 12,
    padding: '12px 16px',
    marginBottom: 20,
    color: '#4C1D95',
    fontSize: 12,
  },
  // No building selected: most tiles stay locked until one is, so this has to be
  // impossible to miss — a solid red banner with an icon, not a faint hint.
  buildingWarning: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: 12,
    background: '#FDECEC',
    border: '2px solid #C62828',
    borderRadius: 12,
    padding: '14px 20px',
    marginBottom: 20,
    color: '#B71C1C',
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.35,
    '& .MuiSvgIcon-root': { fontSize: 28, color: '#C62828', flexShrink: 0 },
  },
  grid: {
    display: 'grid',
    gap: 24,
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    [theme.breakpoints.down('lg')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  card: {
    background: 'linear-gradient(135deg, #FFF5D8 0%, #FFF0C0 100%)',
    border: '2px solid #F7D37F',
    borderRadius: 18,
    boxShadow: '0 14px 28px rgba(249, 194, 80, 0.25)',
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    height: 420,
    minHeight: 420,
    maxHeight: 420,
    overflow: 'hidden',
  },
  cardHeader: {
    background: 'linear-gradient(135deg, rgba(124,58,237,0.12) 0%, rgba(79,70,229,0.12) 100%)',
    borderBottom: '2px solid rgba(124,58,237,0.25)',
    margin: '-20px -20px 16px',
    padding: '16px 20px',
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    minHeight: 69,
    boxSizing: 'border-box',
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    textTransform: 'uppercase',
    fontSize: 19,
    fontWeight: 700,
    color: '#5B21B6',
    flex: 1,
  },
  helpIcon: {
    fontSize: 16,
    color: '#7C3AED',
    cursor: 'help',
  },
  addButton: {
    background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
    color: '#fff',
    borderRadius: 999,
    textTransform: 'none',
    fontSize: 15,
    fontWeight: 700,
    padding: '6px 14px',
    minWidth: 0,
    '&:hover': {
      background: 'linear-gradient(135deg, #16A34A 0%, #15803D 100%)',
    },
    '&.Mui-disabled': {
      opacity: 0.5,
      color: '#fff',
    },
  },
  itemGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 150px)',
    justifyContent: 'center',
    gap: 12,
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingRight: 4,
    marginRight: -4,
    alignContent: 'start',
  },
  itemGridScrollable: {
    overflowY: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { width: 0 },
    '&:hover': {
      scrollbarWidth: 'thin',
    },
    '&:hover::-webkit-scrollbar': { width: 6 },
    '&:hover::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(124,58,237,0.4)',
      borderRadius: 3,
    },
  },
  itemButton: {
    padding: 12,
    borderRadius: 12,
    border: '1px solid rgba(250, 204, 120, 0.7)',
    backgroundColor: 'rgba(255,255,255,0.6)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    maxWidth: 150,
    height: 150,
    transition: 'all 0.2s ease',
    textAlign: 'center',
    '&:hover': {
      backgroundColor: 'rgba(255, 238, 200, 0.9)',
      transform: 'translateY(-2px)',
      boxShadow: '0 10px 20px rgba(180, 83, 9, 0.12)',
    },
    '&.Mui-disabled': {
      opacity: 0.45,
      cursor: 'not-allowed',
      transform: 'none',
      boxShadow: 'none',
    },
  },
  itemIcon: {
    width: 52,
    height: 52,
    borderRadius: 13,
    background: 'linear-gradient(135deg, #ceb8f2 0%, #5f58e6 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    boxShadow: '0 8px 14px rgba(79, 70, 229, 0.3)',
  },
  itemIconAction: {
    background: 'linear-gradient(135deg, #48c18f 0%, #94ca8c 50%)',
    boxShadow: '0 2px 14px rgba(34, 197, 94, 0.35)',
  },
  itemLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#3D2A00',
    lineHeight: 1.2,
  },
  itemWrapper: {
    width: '100%',
  },
  itemWrapperSpan: {
    display: 'block',
    width: '100%',
  },
  viewToggle: {
    display: 'flex',
    flexShrink: 0,
    borderRadius: 999,
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.65)',
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
  viewToggleButton: {
    padding: '6px 16px',
    fontWeight: 700,
    fontSize: 13,
    color: 'rgba(255,255,255,0.7)',
    textTransform: 'none',
    border: 'none',
    borderRadius: 0,
    minWidth: 0,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.15)',
    },
  },
  viewToggleActive: {
    backgroundColor: 'rgba(255,255,255,0.25)',
    color: '#fff',
  },
}))

// Building-section tiles carry scope-relative paths; navigation prefixes
// /b/:bId. Exact-or-slash matching so '/workOrderDashboardGlobal' (portfolio)
// never matches '/workOrderDashboard'; queries ('/visitorLog?type=…') survive.
const BUILDING_SECTION_PREFIXES = [
  '/buildingInfo',
  '/analytics',
  '/contacts',
  '/library',
  '/calendar',
  '/dataArchive',
  '/visitorLog',
  '/notificationEngine',
  '/alerts',
  '/reports',
  '/subscriptions',
  '/workOrderDashboard',
  '/workOrderIntelligence',
  '/accountingDashboard',
  '/masterNotepadDashboard',
  '/collectionDashboard',
  '/deficiencyTracker',
  '/marketing',
]
const isBuildingSectionPath = (path: string) => {
  const pathname = path.split('?')[0]
  return BUILDING_SECTION_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
}

const VIEW_STORAGE_KEY = 'activate.dashboard.activeView'
const readStoredView = (): 'portfolio' | 'building' | 'activate' => {
  try {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY)
    if (stored === 'portfolio' || stored === 'activate') return stored
  } catch (e) {
    /* storage unavailable */
  }
  return 'building'
}

const DashboardNew: React.FC<DashboardViewProps> = ({ onToggleTheme, isNewTheme }) => {
  const classes = useStyles()
  const redirectTo = useRedirect()
  const dispatch = useDispatch()
  const { updateGroup } = useContext(AppContext)
  const [apiManagerOpen, setApiManagerOpen] = useState(false)
  const [newEquipmentOpen, setNewEquipmentOpen] = useState(false)
  // Restored from the last toggle so the launcher reopens on the section the user was
  // in (Portfolio / Building / Activate) after "Back to Home", not always on Building.
  const [activeView, setActiveView] = useState<'portfolio' | 'building' | 'activate'>(readStoredView)
  const [searchQuery, setSearchQuery] = useState('')
  const [buildingType, setBuildingType] = useState('')
  const [managementTagOptions, setManagementTagOptions] = useState<{ TagId: string; Name: string }[]>([])
  const [locale] = useLocaleState()
  const { selectedPropertyId, selectedGroupId, selectedManagementTag, user } = useSelector(
    (state: any) => state.profile
  )
  // Same derivation as the legacy navbar (AppBarExtend): unique ManagementTags
  // from the permission-scoped building list PropertySearch already fetches.
  const [tagsResolved, setTagsResolved] = useState(false)
  const handleEntitiesLoaded = useCallback((entities: IEntity[]) => {
    const tagsById = new Map<string, string>()
    entities.forEach((entity) => {
      const { TagId, Name } = entity.ManagementTag || {}
      if (TagId && Name) tagsById.set(TagId, Name)
    })
    setManagementTagOptions(
      Array.from(tagsById, ([TagId, Name]) => ({ TagId, Name })).sort((a, b) => a.Name.localeCompare(b.Name))
    )
    setTagsResolved(true)
  }, [])
  // Mirrors AppBarExtend: a selection whose tag is no longer on any building would keep
  // filtering the building picker down to nothing while showing an empty box.
  useEffect(() => {
    if (!tagsResolved || !selectedManagementTag?.TagId) return
    if (managementTagOptions.some((tag) => tag.TagId === selectedManagementTag.TagId)) return
    dispatch(setManagementTag({ managementTag: undefined }))
  }, [tagsResolved, managementTagOptions, selectedManagementTag, dispatch])

  // Portfolio tiles are group-wide, so a building carried over from the Building view
  // would only scope the app bar to something the section ignores. Drop it (#3492).
  const clearBuilding = useCallback(() => {
    dispatch(selectedProperty({ propertyId: '', propertyName: '', buildingCode: '', propertyTag: {} }))
  }, [dispatch])
  const switchView = useCallback(
    (view: 'portfolio' | 'building' | 'activate') => {
      setActiveView(view)
      if (view === 'portfolio') clearBuilding()
      try {
        localStorage.setItem(VIEW_STORAGE_KEY, view)
      } catch (e) {
        /* storage unavailable */
      }
    },
    [clearBuilding]
  )
  // The launcher can also reopen straight on Portfolio (restored view) with a building
  // picked elsewhere in the meantime; same rule on arrival.
  useEffect(() => {
    if (activeView === 'portfolio' && selectedPropertyId) clearBuilding()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const permissions = usePermissions()
  // null until the hash lands (fresh tab recovering via CheckIn) — distinct from
  // a loaded hash that lacks a key. See getDisabledReason.
  const permissionsLoaded = useSelector((state: any) => !!state.auth.permissions)
  const { GlobalManageCustomers, PortfolioTeam } = permissions
  const showWelcome = !!GlobalManageCustomers && !selectedGroupId
  // A restored view whose toggle this user does not get (Portfolio needs PortfolioTeam or
  // GlobalManageCustomers, Activate needs GlobalManageCustomers) drops back to Building,
  // but only once the permission hash has landed: before that every key reads false.
  useEffect(() => {
    if (!permissionsLoaded || activeView === 'building') return
    const allowed = activeView === 'portfolio' ? !!(PortfolioTeam || GlobalManageCustomers) : !!GlobalManageCustomers
    if (!allowed) switchView('building')
  }, [permissionsLoaded, activeView, PortfolioTeam, GlobalManageCustomers, switchView])

  useEffect(() => {
    if (selectedPropertyId) {
      getPropertyDetail({ eId: selectedPropertyId }).then((res: any) => {
        setBuildingType(res?.BuildingType || '')
      })
    } else {
      setBuildingType('')
    }
  }, [selectedPropertyId])

  const isOffice = buildingType === 'Office'
  const isIndustrial = buildingType === 'Industrial'

  const activeConfig = useMemo(() => {
    if (activeView === 'portfolio') return [PORTFOLIO_CARD, PORTFOLIO_ANALYTICS_CARD]
    if (activeView === 'activate') return [ACTIVATE_CARD, ACTIVATE_ADMIN_CARD]
    return CATEGORY_CONFIG.map((category) => ({
      ...category,
      items: category.items.filter((item) => {
        if (isOffice && item.excludeFromOffice) return false
        if (isIndustrial && item.excludeFromIndustrial) return false
        return true
      }),
    }))
  }, [activeView, isOffice, isIndustrial])

  const isSearching = !!searchQuery.trim()
  /**
   * Search over the tiles: 3 = a tile-name word starts the query, 2 = the
   * query is somewhere in the tile name, 1 = the query only matches through
   * the category title (a category-name search keeps its whole card). Tiles
   * sort by score inside their card; empty cards hide; cards sort by their
   * best tile, then by how many matched.
   */
  const searchedConfig = useMemo(() => {
    const tokens = searchTokens(searchQuery)
    if (!tokens.length) return activeConfig
    return activeConfig
      .map((category) => {
        const scored = category.items
          .map((item) => {
            let score = 0
            if (matchesTokens(item.name, tokens)) {
              score = tokens.some((token) => item.name.toLowerCase().startsWith(token)) ? 3 : 2
            } else if (matchesTokens(`${category.title} ${item.name}`, tokens)) {
              score = 1
            }
            return { item, score }
          })
          .filter((entry) => entry.score > 0)
          .sort((a, b) => b.score - a.score)
        return {
          ...category,
          items: scored.map((entry) => entry.item),
          maxScore: scored[0]?.score ?? 0,
          matchCount: scored.length,
        }
      })
      .filter((category) => category.matchCount > 0)
      .sort((a, b) => b.maxScore - a.maxScore || b.matchCount - a.matchCount)
  }, [activeConfig, searchQuery])

  const isCategory = (item: NavItem | Category): item is Category => {
    return 'items' in item
  }

  const getDisabledReason = (item: NavItem | Category) => {
    const path = isCategory(item) ? item.addPath : item.path
    const requiresProperty = isCategory(item) ? item.addRequiresProperty : item.requiresProperty
    const permissionKey = isCategory(item) ? item.addPermissionKey : item.permissionKey
    if (!path) return 'Coming soon'
    // Building-scoped paths need a building regardless of the hand-set flag:
    // handleNavigate early-returns on them, so an enabled tile would be a
    // silent no-op.
    if ((requiresProperty || isBuildingSectionPath(path)) && !selectedPropertyId)
      return 'Select a building to access this'
    // Fail closed once permissions have loaded: a key the hash never sent is
    // not granted, and treating "absent" as allowed sent users to Access Denied.
    // While the hash is still hydrating every key reads false, so hold off
    // rather than flash "no access" across the whole launcher.
    if (permissionKey && permissionsLoaded && !permissions[permissionKey]) return 'You do not have access'
    return ''
  }

  const handleAddNavigate = useCallback(
    (path?: string) => {
      if (!path) return
      if (isBuildingSectionPath(path)) {
        if (!selectedPropertyId) return
        redirectTo(`/b/${selectedPropertyId}${path}`, undefined, undefined, undefined, { openAdd: true })
        return
      }
      redirectTo(path, undefined, undefined, undefined, { openAdd: true })
    },
    [redirectTo, selectedPropertyId]
  )

  const handleNavigate = useCallback(
    (path?: string) => {
      if (!path) return
      if (path.includes('?openAdd=true')) {
        redirectTo(path.split('?')[0], undefined, undefined, undefined, { openAdd: true })
        return
      }
      if (path.startsWith('/accountCreate') && selectedGroupId) {
        const qs = path.includes('?') ? path.slice(path.indexOf('?')) : ''
        redirectTo(`/accountCreate/${selectedGroupId}${qs}`)
        return
      }
      if (isBuildingSectionPath(path)) {
        if (!selectedPropertyId) return
        redirectTo(`/b/${selectedPropertyId}${path}`)
        return
      }
      redirectTo(path)
    },
    [redirectTo, selectedGroupId, selectedPropertyId]
  )

  const handleLogoClick = useCallback(() => {
    redirectTo('/')
  }, [redirectTo])

  const handleGroupChange = useCallback(
    (group: any) => {
      if (!group) return
      const groupName = group.GroupName?.[locale] || group.GroupName?.en || ''
      dispatch(selectedGroup({ groupId: group.acId, groupName, group }))
      updateGroup({ currentGroupId: group.acId, currentGroupName: groupName })
      dispatch(selectedProperty({ propertyId: '', propertyName: '', buildingCode: '', propertyTag: {} }))
    },
    [dispatch, updateGroup, locale]
  )

  const handlePropertyChange = useCallback(
    (property: any) => {
      if (!property) return
      const propertyName = property.Name?.[locale] || ''
      setBuildingType(property.BuildingType || '')
      dispatch(
        selectedProperty({
          propertyId: property.id,
          propertyName,
          buildingCode: property.Code,
          propertyTag: property.PropertyTag || {},
        })
      )
    },
    [dispatch, locale]
  )

  const handleCloseApiManager = useCallback(() => setApiManagerOpen(false), [])
  const handleCloseNewEquipment = useCallback(() => setNewEquipmentOpen(false), [])

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }, [])
  const handleSearchClear = useCallback(() => setSearchQuery(''), [])
  const handleSearchKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Escape') setSearchQuery('')
  }, [])
  const searchInputProps = useMemo(
    () => ({
      startAdornment: SEARCH_ADORNMENT,
      endAdornment: isSearching ? (
        <InputAdornment position="end">
          <IconButton size="small" onClick={handleSearchClear} aria-label="Clear search">
            <ClearIcon fontSize="small" />
          </IconButton>
        </InputAdornment>
      ) : undefined,
    }),
    [isSearching, handleSearchClear]
  )

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.headerInner}>
          <Box className={classes.logoCluster}>
            <ButtonBase
              className={classes.logoButton}
              onClick={handleLogoClick}
              disableRipple
              aria-label="Go to dashboard"
            >
              <img src={ACTIVATE_COMPANY_LOGO_WHITE} alt="Activate" className={classes.headerLogo} />
            </ButtonBase>
            <Box className={classes.viewToggle}>
              {(PortfolioTeam || GlobalManageCustomers) && (
                <ButtonBase
                  className={classnames(
                    classes.viewToggleButton,
                    activeView === 'portfolio' && classes.viewToggleActive
                  )}
                  onClick={() => switchView('portfolio')}
                >
                  Portfolio
                </ButtonBase>
              )}
              <ButtonBase
                className={classnames(classes.viewToggleButton, activeView === 'building' && classes.viewToggleActive)}
                onClick={() => switchView('building')}
              >
                Building
              </ButtonBase>
              {GlobalManageCustomers && (
                <ButtonBase
                  className={classnames(
                    classes.viewToggleButton,
                    activeView === 'activate' && classes.viewToggleActive
                  )}
                  onClick={() => switchView('activate')}
                >
                  Activate
                </ButtonBase>
              )}
            </Box>
            <Box className={classes.selectors}>
              <AccountSearch
                isBarSearch
                style={SELECTOR_STYLE}
                variant="outlined"
                inputRootClass={classes.selectorInput}
                onChange={handleGroupChange}
              />
              {managementTagOptions.length > 0 && (
                <Autocomplete
                  style={SELECTOR_STYLE}
                  classes={{ inputRoot: classnames(classes.managementTagInput, classes.selectorInput) }}
                  options={managementTagOptions}
                  value={managementTagOptions.find((tag) => tag.TagId === selectedManagementTag?.TagId) ?? null}
                  onChange={(_, tag) => {
                    dispatch(setManagementTag({ managementTag: tag || undefined }))
                  }}
                  getOptionLabel={(tag) => tag.Name || ''}
                  isOptionEqualToValue={(option, value) => option.TagId === value?.TagId}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Property Management" variant="outlined" />
                  )}
                  popupIcon={<ExpandMore />}
                />
              )}
              <PropertySearch
                isBarSearch
                style={SELECTOR_STYLE}
                variant="outlined"
                inputRootClass={classes.selectorInput}
                onChange={handlePropertyChange}
                filterByManagementTag
                onEntitiesLoaded={handleEntitiesLoaded}
                allowedBuildingIds={
                  PortfolioTeam && !GlobalManageCustomers ? user?.UserContext?.AccessBuildingIds : undefined
                }
              />
            </Box>
          </Box>
          <Box className={classes.headerMeta}>
            {/* API Manager — hidden until functionality is implemented
            <Button className={classes.apiButton} startIcon={<SettingsIcon />} onClick={() => setApiManagerOpen(true)}>
              API Manager
            </Button>
            */}
            {/* Back to home — hidden on new navbar
            <Button className={classes.apiButton} startIcon={<HomeIcon />} onClick={() => redirectTo('/')}>
              Back to home
            </Button>
            */}
            <DashboardThemeToggle checked={!!isNewTheme} onChange={onToggleTheme} tone="light" variant="new" />
            <CustomUserMenu logout={<LogoutButton />} />
          </Box>
        </Box>
      </Box>

      {showWelcome && <WelcomeNoPortfolio />}

      {/* Only the Building section's tiles need a building; Portfolio and
          Activate tiles are group-scoped, so the prompt would be noise there. */}
      {!showWelcome && activeView === 'building' && !selectedPropertyId && (
        <Box className={classes.buildingWarning} role="alert">
          <WarningIcon />
          Select a building from the top bar to unlock building-specific tools.
        </Box>
      )}

      {!showWelcome && (
      <>
      <Box className={classes.searchBar}>
        <TextField
          className={classes.searchInput}
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          placeholder="Search pages and actions"
          variant="outlined"
          size="small"
          InputProps={searchInputProps}
          inputProps={{ 'aria-label': 'Search navigation tiles' }}
        />
      </Box>
      {isSearching && searchedConfig.length === 0 && (
        <Box className={classes.notice}>No pages match "{searchQuery.trim()}" — try a different term.</Box>
      )}
      <Box
        className={classes.grid}
        style={
          activeView === 'activate'
            ? GRID_STYLE_ACTIVATE
            : activeView === 'portfolio'
            ? GRID_STYLE_PORTFOLIO
            : undefined
        }
      >
        {searchedConfig.map((category) => (
          <Box
            key={category.title}
            className={classes.card}
            style={
              isSearching
                ? CARD_STYLE_SEARCH
                : activeView === 'activate'
                ? category.items.length > 10
                  ? CARD_STYLE_ACTIVATE
                  : CARD_STYLE_ACTIVATE_COMPACT
                : activeView === 'portfolio'
                ? CARD_STYLE_PORTFOLIO
                : FIXED_HEIGHT_CATEGORIES.has(category.title)
                ? CARD_STYLE_FIXED
                : undefined
            }
          >
            <Box className={classes.cardHeader}>
              <Box className={classes.cardTitle}>
                <category.icon fontSize="small" />
                <span>{category.title}</span>
                <Tooltip title={category.description} placement="top" {...TOOLTIP_PROPS}>
                  <HelpCircleIcon className={classes.helpIcon} />
                </Tooltip>
              </Box>
              {category.addButtonLabel &&
                (() => {
                  const reason = getDisabledReason(category)
                  return (
                    <MaybeTooltip reason={reason}>
                      <Button
                        className={classes.addButton}
                        size="small"
                        startIcon={ADD_ICON_SMALL}
                        onClick={() => {
                          if (category.title === 'EQUIPMENT') {
                            setNewEquipmentOpen(true)
                          } else {
                            handleAddNavigate(category.addPath)
                          }
                        }}
                        disabled={!!reason}
                      >
                        {category.addButtonLabel}
                      </Button>
                    </MaybeTooltip>
                  )
                })()}
              {category.addButtons && (
                <Box style={FLEX_GAP_8}>
                  {category.addButtons.map((btn: AddButton) => {
                    const reason = btn.requiresProperty && !selectedPropertyId ? 'Select a building to access this' : ''
                    return (
                      <MaybeTooltip key={btn.label} reason={reason}>
                        <Button
                          className={classes.addButton}
                          size="small"
                          startIcon={ADD_ICON_SMALL}
                          onClick={() => handleAddNavigate(btn.path)}
                          disabled={!!reason}
                        >
                          {btn.label}
                        </Button>
                      </MaybeTooltip>
                    )
                  })}
                </Box>
              )}
            </Box>
            <Box
              className={classnames(
                classes.itemGrid,
                activeView === 'building' && category.items.length > 8 && classes.itemGridScrollable
              )}
              style={
                activeView === 'activate'
                  ? ITEM_GRID_ACTIVATE
                  : activeView === 'portfolio'
                  ? category.title === 'ACCOUNT' || category.title === 'ANALYTICS'
                    ? ITEM_GRID_PORTFOLIO_5COL
                    : ITEM_GRID_PORTFOLIO_4COL
                  : activeView === 'building'
                  ? {
                      gridTemplateColumns: 'repeat(4, minmax(0, 120px))',
                      alignContent: category.items.length > 8 ? 'start' : 'center',
                      overflowY: category.items.length > 8 ? 'auto' : 'visible',
                    }
                  : undefined
              }
            >
              {category.items.map((item) => {
                const reason = getDisabledReason(item)
                const useStandardSize =
                  activeView !== 'building' ||
                  FOUR_COL_CATEGORIES.has(category.title) ||
                  THREE_COL_CATEGORIES.has(category.title)
                const content = (
                  <ButtonBase
                    className={classes.itemButton}
                    onClick={() => handleNavigate(item.path)}
                    disabled={!!reason}
                    style={useStandardSize ? STANDARD_ITEM_SIZE : undefined}
                  >
                    <Box
                      className={classnames(classes.itemIcon, item.isAction && classes.itemIconAction)}
                      style={useStandardSize ? STANDARD_ICON_SIZE : undefined}
                    >
                      <item.icon fontSize="small" />
                    </Box>
                    <Typography className={classes.itemLabel}>{item.name}</Typography>
                  </ButtonBase>
                )
                return (
                  <Box key={item.name} className={classes.itemWrapper}>
                    <MaybeTooltip reason={reason} className={classes.itemWrapperSpan}>
                      {content}
                    </MaybeTooltip>
                  </Box>
                )
              })}
            </Box>
          </Box>
        ))}
      </Box>
      <ApiManagerDialog open={apiManagerOpen} onClose={handleCloseApiManager} />
      <NewEquipmentDialog
        open={newEquipmentOpen}
        onClose={handleCloseNewEquipment}
        groupId={selectedGroupId || ''}
        buildingId={selectedPropertyId || ''}
      />
      </>
      )}
    </Box>
  )
}

export default DashboardNew
